import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { config } from "../../../base/constant";
import { RootState } from "../../../base/store/store";

import { recordSalesforceApiAudit, detectSalesforceApiName, getStoredCustomerInfo } from "./sfAuditLogger";

const prepareBaseQuery = (baseUrl: string) => {
    const rawBaseQuery = fetchBaseQuery({
        baseUrl,
        prepareHeaders: (headers, { getState }) => {
            const token = (getState() as RootState).salesforceAuth.token;
            if (token) {
                headers.set("Authorization", token);
            }
            headers.set("Content-Type", "application/json");
            return headers;
        }
    });

    return async (args: any, api: any, extraOptions: any) => {
        const startTime = performance.now();
        const urlStr = typeof args === "string" ? args : args.url;
        const fullUrl = `${baseUrl.replace(/\/+$/, '')}/${urlStr.replace(/^\/+/, '')}`;
        const method = (typeof args === "object" && args.method) || "POST";
        const body = (typeof args === "object" && args.body) || {};

        const result = await rawBaseQuery(args, api, extraOptions);
        const durationMs = Math.round(performance.now() - startTime);

        const dataObj = result.data as any;
        const errObj = result.error as any;

        const isSuccess = !result.error && (!dataObj || (dataObj.status !== "error" && dataObj.status !== "failed"));
        const statusCode = result.error ? (typeof errObj?.status === 'number' ? errObj.status : 500) : 200;

        // Extract customer info from body or store
        const user = getStoredCustomerInfo();
        const userPhone = body.mobile_number || body.customerMobile || user.phone;
        const userEmail = body.email || user.email;

        // Build summary
        let summary = "";
        if (urlStr.includes("documentDownload")) {
            summary = `Document: ${body.doc_name || body.doc_id || 'Unknown'}`;
        } else if (urlStr.includes("customerDetails")) {
            summary = `Details for Unit: ${body.unit_number || '-'} (${body.project_name || '-'})`;
        } else if (urlStr.includes("notifications")) {
            summary = `Notifications check`;
        }

        // Sanitized response for logging (do not transmit huge base64 document data)
        let respDataToLog: any = null;
        if (dataObj) {
            if (urlStr.includes("documentDownload") && dataObj.result?.document_data) {
                respDataToLog = {
                    status: dataObj.status,
                    document_name: dataObj.result.document_name,
                    document_extension: dataObj.result.document_extension,
                    data_length: dataObj.result.document_data?.length
                };
            } else {
                respDataToLog = dataObj;
            }
        }

        recordSalesforceApiAudit({
            api_name: detectSalesforceApiName(urlStr),
            endpoint_url: fullUrl,
            method,
            user_phone: userPhone,
            user_email: userEmail,
            user_name: user.name,
            request_payload: body,
            response_status: isSuccess ? "SUCCESS" : "FAILED",
            status_code: statusCode,
            response_summary: summary,
            response_data: respDataToLog,
            error_message: errObj ? JSON.stringify(errObj.data || errObj) : (dataObj?.message || ""),
            response_time_ms: durationMs,
        });

        return result;
    };
};

export const customerDetailsApi = createApi({
    reducerPath: "customerDetailsApi",

    baseQuery: prepareBaseQuery(config().SALESFORCE_URL),

    endpoints: (builder) => ({
        postSfCustomerDetail: builder.mutation<any, Record<string, any>>({
            query: (customerInfo) => ({
                url: "/customerDetails",
                method: "POST",
                body: customerInfo
            })
        }),

        postSfCustomerNotifications: builder.mutation<any, Record<string, any>>({
            query: (customerInfo) => ({
                url: "/notifications",
                method: "POST",
                body: customerInfo
            })
        })
    })
});

export const customerDocDownloadApi = createApi({
    reducerPath: "customerDocDownloadApi",

    baseQuery: prepareBaseQuery(config().SALESFORCE_DOWNLOAD_API_URL),

    endpoints: (builder) => ({
        postDownloadSFDoc: builder.mutation<any, Record<string, any>>({
            query: (docInfo) => ({
                url: "/documentDownload",
                method: "POST",
                body: docInfo
            })
        })
    })
});

export const {
    usePostSfCustomerDetailMutation,
    usePostSfCustomerNotificationsMutation
} = customerDetailsApi;

export const { usePostDownloadSFDocMutation } =
    customerDocDownloadApi;
